// The jobs store (P1-E05): one `/admin/events` stream per signed-in tab feeds the header pill and the
// dashboard; `useJobStream(id)` follows one job (progress, step, the safe log) for its progress view.
// Envelope: {type, job: {id,type,status,step,progress,eta_seconds,attempts,target,organization_id}, data, at}.
import { useEffect, useState, useSyncExternalStore } from 'react';
import { onAuthProblem } from './auth';
import { api, toApiError, unwrap } from './client';
import { openStream, type SseEvent, type StreamStatus } from './sse';
import type { Job, JobSummary } from './types';

export const TERMINAL_EVENTS = new Set(['job.done', 'job.error', 'job.cancelled']);
export const ACTIVE = new Set(['queued', 'running']);

export type JobEvent = { type: string; job: JobSummary; data: Record<string, unknown>; at: string };
export type LogLine = { at: string; level: string; message: string };

function isJobEvent(e: SseEvent): e is SseEvent & { data: JobEvent } {
  const d = e.data as Partial<JobEvent> | null;
  return !!d && typeof d === 'object' && !!d.job && typeof d.job.id === 'string';
}

function summary(j: Job | JobSummary): JobSummary {
  const { id, type, status, step, progress, eta_seconds, attempts, target, organization_id } = j;
  const label = (j as JobSummary).label;
  return { id, type, status, step, progress, eta_seconds, attempts, target, organization_id, ...(label ? { label } : {}) };
}

// ── The store ────────────────────────────────────────────────────────────────
type State = { jobs: JobSummary[]; status: StreamStatus };
let state: State = { jobs: [], status: 'closed' };
const listeners = new Set<() => void>();
const eventListeners = new Set<(e: JobEvent) => void>();

function set(next: Partial<State>) {
  state = { ...state, ...next };
  listeners.forEach((l) => l());
}

function upsert(j: JobSummary) {
  const i = state.jobs.findIndex((x) => x.id === j.id);
  const jobs = [...state.jobs];
  if (i >= 0) jobs[i] = { ...jobs[i], ...j };
  else jobs.unshift(j);
  set({ jobs });
}

/** Applies one frame of `/admin/events` (exported for tests). */
export function applyEvent(e: SseEvent): void {
  if (e.event === 'snapshot') {
    const list = (e.data as { jobs?: Job[] } | null)?.jobs ?? [];
    // Jobs that finished while we were away stay as they last were (the dashboard shows «Готово»).
    const fresh = list.map(summary);
    const keep = state.jobs.filter((j) => !ACTIVE.has(j.status) && !fresh.some((f) => f.id === j.id));
    set({ jobs: [...fresh, ...keep] });
    return;
  }
  if (!isJobEvent(e)) return;
  upsert(summary(e.data.job));
  eventListeners.forEach((l) => l(e.data));
}

/** Opens `/admin/events` (one per tab; the shell calls it once signed in). Returns the close function. */
export function startJobsStream(): () => void {
  const close = openStream({ path: '/admin/events', onEvent: applyEvent, onStatus: (status) => set({ status }) });
  const off = onAuthProblem((p) => p === 'signed_out' && close());
  return () => {
    off();
    close();
  };
}

/** Seeds the store from a list the page already has (e.g. the dashboard's running_jobs). */
export function seedJobs(list: (Job | JobSummary)[]): void {
  const known = new Set(state.jobs.map((j) => j.id));
  const add = list.filter((j) => !known.has(j.id)).map(summary);
  if (add.length) set({ jobs: [...state.jobs, ...add] });
}

export function resetJobs(): void {
  set({ jobs: [], status: 'closed' });
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useJobs(): JobSummary[] {
  return useSyncExternalStore(subscribe, () => state.jobs);
}

/** Queued and running jobs: the header pill. */
export function useActiveJobCount(): number {
  return useSyncExternalStore(subscribe, () => state.jobs.filter((j) => ACTIVE.has(j.status)).length);
}

export function useStreamStatus(): StreamStatus {
  return useSyncExternalStore(subscribe, () => state.status);
}

/** Every job event, e.g. to refetch a screen when its job finishes. */
export function useJobEvents(listener: (e: JobEvent) => void): void {
  useEffect(() => {
    eventListeners.add(listener);
    return () => {
      eventListeners.delete(listener);
    };
  }, [listener]);
}

// ── One job ──────────────────────────────────────────────────────────────────
export type JobStream = { job: JobSummary | null; log: LogLine[]; live: boolean; finished: boolean };

/**
 * Follows `/admin/jobs/{id}/events`: the snapshot gives the job and its safe log, then `job.log` lines,
 * progress and step updates arrive; a terminal event ends it. A reload mid-job resumes from the snapshot.
 */
export function useJobStream(jobId: string | null | undefined): JobStream {
  const [s, setS] = useState<JobStream>({ job: null, log: [], live: false, finished: false });
  useEffect(() => {
    if (!jobId) return;
    setS({ job: null, log: [], live: false, finished: false });
    return openStream({
      path: `/admin/jobs/${jobId}/events`,
      isTerminal: (e) => TERMINAL_EVENTS.has(e.event),
      onStatus: (st) => setS((p) => ({ ...p, live: st === 'open' && !p.finished })),
      onEvent: (e) => {
        if (e.event === 'snapshot') {
          const job = (e.data as { job?: Job } | null)?.job;
          if (!job) return;
          const done = !ACTIVE.has(job.status);
          setS((p) => ({ ...p, job: summary(job), log: (job.safe_log as LogLine[]) ?? [], finished: done, live: !done }));
          return;
        }
        if (!isJobEvent(e)) return;
        const ev = e.data;
        setS((p) => ({
          ...p,
          job: summary(ev.job),
          log: ev.type === 'job.log' ? [...p.log, ev.data as unknown as LogLine] : p.log,
          finished: TERMINAL_EVENTS.has(ev.type),
          live: !TERMINAL_EVENTS.has(ev.type),
        }));
      },
    });
  }, [jobId]);
  return s;
}

const JOB_DONE = new Set(['succeeded', 'failed', 'cancelled', 'dead']);

/**
 * Follows one job until it ends (GET /admin/jobs/{id} every `everyMs`) and returns its `result`;
 * throws an ApiError with the job's error code when it did not succeed (AI mode, chat edits).
 */
export async function waitForJob(jobId: string, everyMs = 1000): Promise<Record<string, unknown>> {
  for (;;) {
    const job = await unwrap(api.GET('/admin/jobs/{job_id}', { params: { path: { job_id: jobId } } }));
    if (JOB_DONE.has(job.status)) {
      const code = job.retryable_error ?? 'job_failed';
      if (job.status !== 'succeeded') throw toApiError(409, { code, i18n_key: `errors.${code}`, params: {}, message: job.status, request_id: null });
      return (job.result ?? {}) as Record<string, unknown>;
    }
    await new Promise((r) => setTimeout(r, everyMs));
  }
}
